import {
  suggestOrgSlug,
  type CreateSiteRequest,
  type UpdateSiteRequest,
} from '@gb-ticket/shared-types';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

export type SiteFormValues = {
  id?: string;
  name: string;
  slug: string;
  isActive: boolean;
};

type SiteFormDialogProps = {
  open: boolean;
  mode: 'create' | 'edit';
  initial: SiteFormValues | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: { create?: CreateSiteRequest; update?: UpdateSiteRequest }) => Promise<void>;
};

export function SiteFormDialog({
  open,
  mode,
  initial,
  busy,
  onOpenChange,
  onSubmit,
}: SiteFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {open ? (
          <SiteFormFields
            key={`${mode}:${initial?.id ?? 'new'}`}
            mode={mode}
            initial={initial}
            busy={busy}
            onOpenChange={onOpenChange}
            onSubmit={onSubmit}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function SiteFormFields({
  mode,
  initial,
  busy,
  onOpenChange,
  onSubmit,
}: Omit<SiteFormDialogProps, 'open'>) {
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(mode === 'edit');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  const canSubmit = name.trim().length >= 2 && slug.trim().length >= 2;

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {mode === 'create' ? 'Standort hinzufügen' : 'Standort bearbeiten'}
        </DialogTitle>
        <DialogDescription>Büro oder Einsatzort.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="site-name">Name</Label>
          <Input
            id="site-name"
            value={name}
            onChange={(event) => {
              const next = event.target.value;
              setName(next);
              if (!slugTouched) {
                setSlug(suggestOrgSlug(next));
              }
            }}
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="site-slug">Kürzel (Slug)</Label>
          <Input
            id="site-slug"
            value={slug}
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(event.target.value.toLowerCase());
            }}
            autoComplete="off"
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Nur Kleinbuchstaben, Ziffern und Bindestriche.
          </p>
        </div>
        {mode === 'edit' && (
          <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
            <div>
              <Label htmlFor="site-active">Aktiv</Label>
              <p className="text-xs text-muted-foreground">
                Inaktive Standorte erscheinen nicht in neuen Zuweisungen.
              </p>
            </div>
            <Switch id="site-active" checked={isActive} onCheckedChange={setIsActive} />
          </div>
        )}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Abbrechen
        </Button>
        <Button
          type="button"
          disabled={busy || !canSubmit}
          onClick={() => {
            if (mode === 'create') {
              void onSubmit({
                create: { name: name.trim(), slug: slug.trim() },
              });
              return;
            }
            void onSubmit({
              update: {
                name: name.trim(),
                slug: slug.trim(),
                isActive,
              },
            });
          }}
        >
          {mode === 'create' ? 'Anlegen' : 'Speichern'}
        </Button>
      </DialogFooter>
    </>
  );
}
